import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { db } from "../../firebase";

const functions = getFunctions(undefined, "us-central1");

const ACTIVE_STATUSES = ["open", "pending", "under_review"];

function normalize(s) {
  return String(s ?? "").trim().toLowerCase();
}

function formatTs(value) {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

function createdAtMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") {
    try {
      return value.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().getTime();
    } catch {
      return 0;
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    return Number(value.seconds) * 1000;
  }
  if (typeof value === "number") return value;
  return 0;
}

function shorten(str, start = 10, end = 8) {
  const s = String(str || "");
  if (s.length <= start + end + 3) return s || "—";
  return `${s.slice(0, start)}…${s.slice(-end)}`;
}

function badgeClass(status) {
  const s = normalize(status);
  if (s === "open") return "admin1-support__badge admin1-support__badge--open";
  if (s === "pending") return "admin1-support__badge admin1-support__badge--pending";
  if (s === "under_review") {
    return "admin1-support__badge admin1-support__badge--review";
  }
  return "admin1-support__badge admin1-support__badge--muted";
}

export default function SupportPage() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingId, setWorkingId] = useState(null);
  const [feedbackById, setFeedbackById] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, "supportTickets"),
      where("status", "in", ACTIVE_STATUSES)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        list.sort(
          (a, b) =>
            createdAtMs(b.updatedAt ?? b.createdAt) -
            createdAtMs(a.updatedAt ?? a.createdAt)
        );
        setTickets(list);
        setLoading(false);
      },
      (err) => {
        console.error("SupportPage snapshot error:", err);
        setTickets([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    return tickets.filter((t) => {
      if (statusFilter !== "all") {
        if (normalize(t.status) !== normalize(statusFilter)) return false;
      }
      if (typeFilter !== "all") {
        if (normalize(t.type || "") !== normalize(typeFilter)) return false;
      }
      if (priorityFilter !== "all") {
        if (normalize(t.priority || "") !== normalize(priorityFilter)) {
          return false;
        }
      }
      return true;
    });
  }, [tickets, statusFilter, typeFilter, priorityFilter]);

  async function markUnderReview(ticket) {
    const id = ticket.id;
    setWorkingId(id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const fn = httpsCallable(functions, "markSupportTicketUnderReview");
      await fn({ ticketId: id });
    } catch (error) {
      console.error("markSupportTicketUnderReview:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  async function closeTicket(ticket) {
    const id = ticket.id;
    setWorkingId(id);
    setFeedbackById((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

    try {
      const closeFn = httpsCallable(functions, "closeSupportTicket");
      await closeFn({ ticketId: id });
    } catch (error) {
      console.error("closeSupportTicket:", error);
      setFeedbackById((prev) => ({
        ...prev,
        [id]: callableErrorMessage(error),
      }));
    } finally {
      setWorkingId(null);
    }
  }

  const busy = workingId != null;

  return (
    <div className="admin1-dashboard">
      <header className="admin1-dashboard__header">
        <h1 className="admin1-dashboard__title">
          {t("admin.support.list.title", { defaultValue: "Support tickets" })}
        </h1>
        <p className="admin1-dashboard__subtitle">
          {t("admin.support.list.subtitle_prefix", { defaultValue: "Active tickets:" })}{" "}
          <span className="admin1-users__kbd">open | pending | under_review</span>
          . {t("admin.support.list.subtitle_actions", {
            defaultValue: "Status changes go only through",
          })}{" "}
          <span className="admin1-users__kbd">markSupportTicketUnderReview</span>{" "}
          {t("common.and", { defaultValue: "and" })}{" "}
          <span className="admin1-users__kbd">closeSupportTicket</span>.
        </p>
      </header>

      <div className="admin1-users__toolbar admin1-support__toolbar">
        <label className="admin1-users__field">
          <span className="admin1-users__label">
            {t("admin.support.filters.status", { defaultValue: "Status" })}
          </span>
          <select
            className="admin1-users__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">{t("admin.support.filters.all", { defaultValue: "All" })}</option>
            <option value="open">{t("support.status.open", { defaultValue: "open" })}</option>
            <option value="pending">{t("support.status.pending", { defaultValue: "pending" })}</option>
            <option value="under_review">
              {t("support.status.under_review", { defaultValue: "under_review" })}
            </option>
          </select>
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">
            {t("admin.support.filters.type", { defaultValue: "Type" })}
          </span>
          <select
            className="admin1-users__select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">{t("admin.support.filters.all", { defaultValue: "All" })}</option>
            <option value="issue">{t("support.types.issue", { defaultValue: "issue" })}</option>
            <option value="contact">{t("support.types.contact", { defaultValue: "contact" })}</option>
          </select>
        </label>
        <label className="admin1-users__field">
          <span className="admin1-users__label">
            {t("admin.support.filters.priority", { defaultValue: "Priority" })}
          </span>
          <select
            className="admin1-users__select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">{t("admin.support.filters.all", { defaultValue: "All" })}</option>
            <option value="low">{t("support.priority_levels.low", { defaultValue: "low" })}</option>
            <option value="normal">{t("support.priority_levels.normal", { defaultValue: "normal" })}</option>
            <option value="high">{t("support.priority_levels.high", { defaultValue: "high" })}</option>
            <option value="urgent">{t("support.priority_levels.urgent", { defaultValue: "urgent" })}</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="admin1-state-card">
          {t("admin.support.list.loading_tickets", { defaultValue: "Loading tickets..." })}
        </div>
      ) : filtered.length === 0 ? (
        <div className="admin1-state-card">
          {tickets.length === 0
            ? t("admin.support.list.empty_queue", {
                defaultValue: "No active support tickets in this queue.",
              })
            : t("admin.support.list.empty_filtered", {
                defaultValue: "No tickets match the filters.",
              })}
        </div>
      ) : (
        <section className="admin1-support__grid">
          {filtered.map((ticket) => {
            const id = ticket.id;
            const uid = String(ticket.uid || "").trim();
            const isWorking = busy && workingId === id;
            const feedback = feedbackById[id];
            const statusNow = normalize(ticket.status);
            const subject = String(ticket.subject ?? "").trim() || "—";
            const body = String(ticket.message ?? "").trim() || "—";

            return (
              <article key={id} className="admin1-support__card">
                <div className="admin1-support__card-head">
                  <div>
                    <div className="admin1-support__card-title">
                      {t("support.ticket", { defaultValue: "Ticket" })}{" "}
                      <span className="admin1-vq__mono">{shorten(id)}</span>
                    </div>
                    <div className="admin1-vq__mono admin1-vq__mono--full">
                      {id}
                    </div>
                  </div>
                  <span className={badgeClass(ticket.status)}>
                    {ticket.status
                      ? t(`support.status.${String(ticket.status)}`, {
                          defaultValue: String(ticket.status),
                        })
                      : "—"}
                  </span>
                </div>

                <dl className="admin1-vq__dl">
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.uid", { defaultValue: "uid" })}</dt>
                    <dd className="admin1-reports__mono">{uid || "—"}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.type", { defaultValue: "type" })}</dt>
                    <dd>{ticket.type ? String(ticket.type) : "—"}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.priority", { defaultValue: "priority" })}</dt>
                    <dd>
                      {ticket.priority ? String(ticket.priority) : "—"}
                    </dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.subject", { defaultValue: "subject" })}</dt>
                    <dd className="admin1-reports__wrap">{subject}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.message", { defaultValue: "message" })}</dt>
                    <dd className="admin1-reports__wrap">{body}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.createdAt", { defaultValue: "createdAt" })}</dt>
                    <dd>{formatTs(ticket.createdAt)}</dd>
                  </div>
                  <div className="admin1-vq__dl-row">
                    <dt>{t("admin.support.fields.updatedAt", { defaultValue: "updatedAt" })}</dt>
                    <dd>{formatTs(ticket.updatedAt)}</dd>
                  </div>
                </dl>

                <div className="admin1-reports__actions">
                  <Link
                    className="admin1-reports__btn admin1-reports__btn--review"
                    to={`/admin1/support/${encodeURIComponent(id)}`}
                  >
                    {t("support.open_ticket", { defaultValue: "Open ticket" })}
                  </Link>
                  <button
                    type="button"
                    className="admin1-reports__btn admin1-reports__btn--review"
                    disabled={busy || statusNow === "under_review"}
                    onClick={() => markUnderReview(ticket)}
                  >
                    {isWorking
                      ? "…"
                      : t("admin.support.actions.mark_under_review", {
                          defaultValue: "Mark under review",
                        })}
                  </button>
                  <button
                    type="button"
                    className="admin1-reports__btn admin1-reports__btn--close"
                    disabled={busy}
                    onClick={() => closeTicket(ticket)}
                  >
                    {isWorking
                      ? "…"
                      : t("support.close_ticket", { defaultValue: "Close ticket" })}
                  </button>
                  {uid ? (
                    <Link
                      className="admin1-reports__btn admin1-reports__btn--link"
                      to={`/admin1/user/${encodeURIComponent(uid)}`}
                    >
                      {t("admin.support.actions.view_user", { defaultValue: "View user" })}
                    </Link>
                  ) : null}
                </div>

                {feedback ? (
                  <div className="admin1-vq__feedback">{feedback}</div>
                ) : null}
              </article>
            );
          })}
        </section>
      )}

      {!loading && filtered.length > 0 ? (
        <p className="admin1-users__meta">
          {t("admin.support.list.loaded_count", {
            defaultValue: "Showing {{filtered}} of {{total}} loaded tickets",
            filtered: filtered.length,
            total: tickets.length,
          })}
        </p>
      ) : null}
    </div>
  );
}
